import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import QuestionEditorModal from '../components/questionbank/QuestionEditorModal';
import CourseStatsModal from '../components/questionbank/CourseStatsModal';
import CourseQuestionsModal from '../components/questionbank/CourseQuestionsModal';
import ImportQuestionsModal from '../components/questionbank/ImportQuestionsModal';

// Shared react-select style — matches the rest of the app.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
    minHeight: 38,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

/**
 * Question Bank landing page.
 *
 * Shows a course grid (one row per course) with:
 *   - BOS filter — narrows the grid to courses tagged under a given BOS
 *   - Search bar — further narrows by code/name
 *   - Question count badge (clickable → CourseStatsModal)
 *   - Actions: View questions, Add question, Import from Excel
 *
 * BOS info (`BOSID`, `BOSName`) is returned by the backend summary endpoint
 * via a LEFT JOIN on CourseBOSAllocations, so no extra round-trip is needed.
 */
function QuestionBank() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const [courses, setCourses] = useState([]);
  const [bosList, setBosList] = useState([]);
  const [segments, setSegments] = useState([]);
  const [enabledAttributes, setEnabledAttributes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [selectedBOS, setSelectedBOS] = useState(null); // null = all

  const [editorOpen, setEditorOpen] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState(null);
  const [editorDefaultCourseId, setEditorDefaultCourseId] = useState('');

  const [statsCourse, setStatsCourse] = useState(null);
  const [questionsCourse, setQuestionsCourse] = useState(null);
  const [importCourse, setImportCourse] = useState(null);

  // -------------------- Data loading --------------------

  const loadCourseSummary = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/question-bank/courses/${tenantId}/summary`);
      if (res.data.success) setCourses(res.data.data || []);
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const loadBosList = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/bos/dropdown/${tenantId}`);
      if (res.data.success) setBosList(res.data.data || []);
    } catch {
      // Non-fatal — filter just won't show BOS options.
    }
  }, [tenantId]);

  const loadSegments = useCallback(async () => {
    try {
      const res = await api.get('/question-bank/segments');
      if (res.data.success) setSegments(res.data.data || []);
    } catch {
      // Non-fatal.
    }
  }, []);

  const loadEnabledAttributes = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/qp-settings/qb-attributes/${tenantId}`);
      if (res.data.success) {
        setEnabledAttributes(
          (res.data.data || [])
            .filter((a) => a.IsEnabled)
            .map((a) => a.AttributeCode)
        );
      }
    } catch {
      // Non-fatal.
    }
  }, [tenantId]);

  useEffect(() => {
    loadCourseSummary();
    loadBosList();
    loadSegments();
    loadEnabledAttributes();
  }, [loadCourseSummary, loadBosList, loadSegments, loadEnabledAttributes]);

  // -------------------- BOS dropdown options --------------------

  // "All BOS" sentinel + one entry per BOS + "Unassigned" at the bottom.
  const bosOptions = useMemo(() => {
    const opts = bosList.map((b) => ({
      value: b.BOSID,
      label: b.BOSName,
    }));
    if (opts.length > 0) {
      opts.push({ value: '__unassigned__', label: '— Courses not assigned to any BOS' });
    }
    return opts;
  }, [bosList]);

  // -------------------- Filtering --------------------

  const filteredCourses = useMemo(() => {
    let result = courses;

    // BOS filter
    if (selectedBOS) {
      if (selectedBOS.value === '__unassigned__') {
        result = result.filter((c) => !c.BOSID);
      } else {
        result = result.filter(
          (c) => String(c.BOSID) === String(selectedBOS.value)
        );
      }
    }

    // Text search
    const q = searchInput.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (c) =>
          (c.CourseCode || '').toLowerCase().includes(q) ||
          (c.CourseName || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [courses, selectedBOS, searchInput]);

  // Totals: always show totals for the *filtered* set so the pills stay in
  // sync with what the user sees in the grid. The "/ N" denominator uses the
  // total from the full (unfiltered) course list for context.
  const totals = useMemo(() => {
    let totalQs = 0;
    let activeQs = 0;
    let coursesWithQs = 0;
    for (const c of filteredCourses) {
      totalQs += Number(c.QuestionCount) || 0;
      activeQs += Number(c.ActiveCount) || 0;
      if ((Number(c.QuestionCount) || 0) > 0) coursesWithQs += 1;
    }
    return { totalQs, activeQs, coursesWithQs };
  }, [filteredCourses]);

  // -------------------- Actions --------------------

  const openAddQuestion = (defaultCourseId = '') => {
    if (segments.length === 0) {
      toast.error('No question segments configured. Contact platform admin.');
      return;
    }
    setEditQuestionId(null);
    setEditorDefaultCourseId(defaultCourseId);
    setEditorOpen(true);
  };

  const clearFilters = () => {
    setSelectedBOS(null);
    setSearchInput('');
  };

  const isFiltered = selectedBOS || searchInput.trim();

  return (
    <div className="page-card">
      <div
        className="page-card-header"
        style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 16 }}
      >
        <h3 style={{ margin: 0 }}>
          Question Bank
          {courses.length > 0 && (
            <span style={{ fontWeight: 400, fontSize: 14, color: '#616161', marginLeft: 8 }}>
              ({filteredCourses.length}
              {isFiltered && filteredCourses.length !== courses.length
                ? ` of ${courses.length}`
                : ''}{' '}
              {courses.length === 1 ? 'course' : 'courses'})
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-add" onClick={() => openAddQuestion('')}>
            + Add Question
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="qb-summary-pills">
        <div className="qb-summary-pill">
          <span className="qb-summary-pill-label">Total Questions</span>
          <span className="qb-summary-pill-value">{totals.totalQs}</span>
        </div>
        <div className="qb-summary-pill">
          <span className="qb-summary-pill-label">Active Questions</span>
          <span className="qb-summary-pill-value">
            {totals.activeQs}
            <span style={{ color: '#9e9e9e', fontWeight: 500, fontSize: 13 }}>
              {' '}/ {totals.totalQs}
            </span>
          </span>
        </div>
        <div className="qb-summary-pill">
          <span className="qb-summary-pill-label">Courses with Questions</span>
          <span className="qb-summary-pill-value">
            {totals.coursesWithQs}
            <span style={{ color: '#9e9e9e', fontWeight: 500, fontSize: 13 }}>
              {' '}/ {filteredCourses.length}
            </span>
          </span>
        </div>
        <div className="qb-summary-pill qb-summary-pill-attrs">
          <span className="qb-summary-pill-label">Enabled Attributes</span>
          <span className="qb-summary-pill-value" style={{ fontSize: 14 }}>
            {enabledAttributes.length === 0
              ? '—'
              : enabledAttributes.map((c) => c.toUpperCase()).join(' · ')}
          </span>
        </div>
      </div>

      {/* Filters toolbar */}
      <div className="alloc-toolbar" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* BOS filter */}
        {bosList.length > 0 && (
          <div className="form-group" style={{ marginBottom: 0, minWidth: 260, flex: 1 }}>
            <label>Filter by BOS</label>
            <Select
              options={bosOptions}
              value={selectedBOS}
              onChange={setSelectedBOS}
              isClearable
              isSearchable
              placeholder="All Boards of Studies..."
              noOptionsMessage={() => 'No BOS found'}
              styles={selectStyles}
            />
          </div>
        )}

        {/* Text search */}
        <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 240 }}>
          <label>Search Courses</label>
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by course code or course name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        {/* Clear filters */}
        {isFiltered && (
          <div style={{ marginBottom: 0, paddingBottom: 1 }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={clearFilters}
              title="Clear all filters"
            >
              ✕ Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Active BOS filter badge */}
      {selectedBOS && (
        <div className="qb-bos-filter-badge">
          <span className="qb-bos-filter-badge-label">Filtered by BOS:</span>
          <span className="qb-bos-filter-badge-name">{selectedBOS.label}</span>
          <button
            className="qb-bos-filter-badge-clear"
            onClick={() => setSelectedBOS(null)}
            title="Remove BOS filter"
          >
            ×
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading…</div>
      ) : filteredCourses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          {courses.length === 0
            ? 'No courses available. Create courses under Course Management first.'
            : isFiltered
            ? 'No courses match the current filters.'
            : 'No courses match your search.'}
          {isFiltered && (
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th style={{ width: 130 }}>Course Code</th>
              <th>Course Name</th>
              <th style={{ width: 180 }}>BOS</th>
              <th style={{ width: 160, textAlign: 'center' }}>Questions</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCourses.map((c) => (
              <tr key={c.CourseID}>
                <td>
                  <strong>{c.CourseCode}</strong>
                  {!c.IsActive && (
                    <span className="badge badge-inactive" style={{ marginLeft: 8 }}>
                      Inactive
                    </span>
                  )}
                </td>
                <td>{c.CourseName}</td>
                <td>
                  {c.BOSName ? (
                    <span className="qb-bos-tag">{c.BOSName}</span>
                  ) : (
                    <span style={{ color: '#bdbdbd', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {c.QuestionCount > 0 ? (
                    <button
                      type="button"
                      className="qb-count-chip"
                      onClick={() => setStatsCourse(c)}
                      title="View statistical summary"
                    >
                      <span className="qb-count-chip-total">{c.QuestionCount}</span>
                      <span className="qb-count-chip-sep">·</span>
                      <span className="qb-count-chip-active" title="Active">
                        {c.ActiveCount} active
                      </span>
                    </button>
                  ) : (
                    <span style={{ color: '#bdbdbd', fontSize: 12 }}>No questions</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn"
                      title="View / Edit Questions"
                      onClick={() => setQuestionsCourse(c)}
                      style={{ background: '#e3f2fd', color: '#1565c0' }}
                    >
                      📋
                    </button>
                    <button
                      className="action-btn action-edit"
                      title="Add Question"
                      onClick={() => openAddQuestion(c.CourseID)}
                    >
                      ＋
                    </button>
                    <button
                      className="action-btn"
                      title="Import from Excel"
                      onClick={() => setImportCourse(c)}
                      style={{ background: '#f3e5f5', color: '#6a1b9a' }}
                    >
                      ⬆
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modals */}
      {editorOpen && (
        <QuestionEditorModal
          tenantId={tenantId}
          questionId={editQuestionId}
          defaultCourseId={editorDefaultCourseId}
          courses={courses}
          segments={segments}
          enabledAttributes={enabledAttributes}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            loadCourseSummary();
          }}
        />
      )}

      {statsCourse && (
        <CourseStatsModal
          tenantId={tenantId}
          course={statsCourse}
          onClose={() => setStatsCourse(null)}
        />
      )}

      {questionsCourse && (
        <CourseQuestionsModal
          tenantId={tenantId}
          course={questionsCourse}
          segments={segments}
          enabledAttributes={enabledAttributes}
          onClose={() => setQuestionsCourse(null)}
          onChanged={loadCourseSummary}
        />
      )}

      {importCourse && (
        <ImportQuestionsModal
          tenantId={tenantId}
          course={importCourse}
          onClose={() => setImportCourse(null)}
          onImported={loadCourseSummary}
        />
      )}
    </div>
  );
}

export default QuestionBank;
